/**
 * ResmiTatilPanel — Admin yönetim panelinde dini + şirket + özel tatilleri CRUD.
 *
 * Sistem tatilleri (ulusal + hardcoded dini bayramlar) read-only olarak görünür.
 * Admin yeni tatil ekler, DB kayıtlarını siler veya düzenler. Değişiklik yıllık izin
 * takvimi ile iş günü hesaplamasında otomatik yansır.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, Plus, Trash2, Edit2, X, Lock, Download, CheckCircle2, Hourglass, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { DateInput } from "@/components/DateInput";

const KIND_LABELS = {
  national: "Resmi Tatil",
  religious: "Dini Bayram",
  company: "Şirket Tatili",
  custom: "Özel Tatil",
};
const KIND_COLORS = {
  national: "bg-rose-100 text-rose-800 border-rose-300",
  religious: "bg-indigo-100 text-indigo-800 border-indigo-300",
  company: "bg-teal-100 text-teal-800 border-teal-300",
  custom: "bg-amber-100 text-amber-800 border-amber-300",
};

export default function ResmiTatilPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editing, setEditing] = useState(null);
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear());
  const [arefeOn, setArefeOn] = useState(false);
  const [arefeToggling, setArefeToggling] = useState(false);
  const [sync, setSync] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, s, syn] = await Promise.all([
        api.get("/holidays"),
        api.get("/holidays/settings").catch(() => ({ data: { arefe_half_day: false } })),
        api.get("/holidays/sync-status").catch(() => ({ data: null })),
      ]);
      setRows(r.data.rows || []);
      setArefeOn(!!s.data?.arefe_half_day);
      setSync(syn.data || null);
    } catch {
      toast.error("Tatiller yüklenemedi");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleArefe = async () => {
    setArefeToggling(true);
    try {
      const next = !arefeOn;
      await api.patch("/holidays/settings", { arefe_half_day: next });
      setArefeOn(next);
      toast.success(next
        ? "Arefe yarım gün AKTIF — iş günü hesabı güncellendi"
        : "Arefe yarım gün KAPATILDI — tam gün olarak sayılacak");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Güncellenemedi");
    } finally { setArefeToggling(false); }
  };

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (r.recurring) return true;
      const y = String(r.date).slice(0, 4);
      return y === String(yearFilter);
    });
  }, [rows, yearFilter]);

  const handleDelete = async (r) => {
    if (r.system) { toast.error("Sistem tatilleri silinemez"); return; }
    if (!window.confirm(`"${r.name}" tatili silinecek. Onaylıyor musunuz?`)) return;
    try {
      await api.delete(`/holidays/${r.id}`);
      toast.success("Silindi");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi");
    }
  };

  const systemCount = rows.filter((r) => r.system).length;
  const customCount = rows.filter((r) => !r.system).length;

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="resmi-tatil-panel">
      <div className="flex items-start gap-2 mb-3 flex-wrap">
        <CalendarDays size={16} className="text-rose-700 mt-0.5" />
        <div className="flex-1 min-w-[180px]">
          <h4 className="font-semibold text-sm text-slate-900">Resmi Tatil Yönetimi</h4>
          <p className="text-xs text-slate-600 mt-0.5">
            Dini bayramlar, şirket tatilleri ve özel günleri ekle. Yıllık izin takviminde ve
            <strong> iş günü hesabında</strong> otomatik yansır.
            Sistem tatilleri <Lock size={10} className="inline"/> ile işaretlidir, salt okunur.
          </p>
          <div className="text-[10px] text-slate-500 mt-1">
            {systemCount} sistem · {customCount} özel · toplam {rows.length}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={yearFilter}
            onChange={(e) => setYearFilter(parseInt(e.target.value, 10))}
            className="px-2 py-1.5 border border-slate-300 rounded-md text-xs"
            data-testid="holiday-year-filter"
          >
            {[2025, 2026, 2027, 2028].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button
            onClick={() => setShowImport(true)}
            data-testid="holiday-import-btn"
            className="inline-flex items-center gap-1 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-3 py-1.5 rounded-md"
            title="Diyanet takviminden dini bayramları toplu içe aktar"
          >
            <Download size={13}/> Toplu Tatil İçe Aktar
          </button>
          <button
            onClick={() => { setEditing(null); setShowAdd(true); }}
            data-testid="holiday-add-btn"
            className="inline-flex items-center gap-1 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold px-3 py-1.5 rounded-md"
          >
            <Plus size={13}/> Tatil Ekle
          </button>
        </div>
      </div>

      {sync?.needs_sync && (
        <div
          className="mb-3 flex items-start gap-2 bg-amber-50 border border-amber-300 rounded-md px-3 py-2"
          data-testid="holiday-sync-badge"
        >
          <Sparkles size={14} className="text-amber-700 mt-0.5" />
          <div className="flex-1 text-xs text-amber-900">
            <strong>Yeni Veri Hazır — Diyanet Senkron</strong>
            <div className="text-amber-800 mt-0.5">
              Diyanet preseti şu yıl(lar) için eksik:{" "}
              <strong>{sync.relevant_missing_years.join(", ")}</strong>. Tek tıkla içe aktarın.
            </div>
          </div>
          <button
            onClick={() => setShowImport(true)}
            data-testid="holiday-sync-import-btn"
            className="inline-flex items-center gap-1 bg-amber-600 hover:bg-amber-700 text-white text-[11px] font-bold px-2.5 py-1 rounded-md"
          >
            <Download size={11}/> İçe Aktar
          </button>
        </div>
      )}

      <label
        className="mb-3 flex items-start gap-2 bg-indigo-50 border border-indigo-200 rounded-md px-3 py-2 cursor-pointer select-none"
        data-testid="arefe-half-day-toggle"
      >
        <Hourglass size={14} className="text-indigo-700 mt-0.5" />
        <div className="flex-1 text-xs text-indigo-900">
          <strong>Arefe Yarım Gün Sayılsın</strong>
          <div className="text-indigo-800 mt-0.5">
            Dini bayramların arefe günleri, iş günü hesabında <strong>0,5 iş günü</strong> olarak kaydedilir.
            Hafta içine denk gelen arefeler için izin düşümü yarım yapılır.
          </div>
        </div>
        <input
          type="checkbox"
          checked={arefeOn}
          disabled={arefeToggling}
          onChange={toggleArefe}
          className="mt-1 h-4 w-4"
          data-testid="arefe-half-day-input"
        />
      </label>

      <div className="max-h-80 overflow-y-auto bg-white border border-slate-200 rounded-md">
        {loading && <div className="text-xs italic text-slate-400 p-3 text-center">Yükleniyor...</div>}
        {!loading && filtered.length === 0 && <div className="text-xs italic text-slate-400 p-3 text-center">Bu yıl için tatil yok</div>}
        {!loading && filtered.map((r) => (
          <div
            key={r.id}
            className={`flex items-center gap-2 px-3 py-1.5 border-b border-slate-100 last:border-b-0 text-xs ${r.system ? "bg-slate-50/60" : ""}`}
            data-testid={`holiday-row-${r.id}`}
          >
            <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded border ${KIND_COLORS[r.kind] || "bg-slate-100 text-slate-700 border-slate-300"}`}>
              {KIND_LABELS[r.kind] || r.kind}
            </span>
            <span className="font-mono font-semibold text-slate-700 min-w-[90px]">{r.date}</span>
            {r.recurring && <span className="inline-flex items-center text-[9px] font-bold bg-sky-100 text-sky-800 border border-sky-200 px-1 rounded">YILLIK</span>}
            <span className="flex-1 font-semibold text-slate-900">{r.name}</span>
            {r.system ? (
              <Lock size={11} className="text-slate-400" />
            ) : (
              <>
                <button
                  onClick={() => { setEditing(r); setShowAdd(true); }}
                  data-testid={`holiday-edit-${r.id}`}
                  className="w-6 h-6 grid place-items-center text-indigo-600 hover:bg-indigo-50 rounded"
                  title="Düzenle"
                >
                  <Edit2 size={11}/>
                </button>
                <button
                  onClick={() => handleDelete(r)}
                  data-testid={`holiday-delete-${r.id}`}
                  className="w-6 h-6 grid place-items-center text-rose-600 hover:bg-rose-50 rounded"
                  title="Sil"
                >
                  <Trash2 size={11}/>
                </button>
              </>
            )}
          </div>
        ))}
      </div>

      {showAdd && (
        <HolidayModal
          editing={editing}
          onClose={() => { setShowAdd(false); setEditing(null); }}
          onSaved={() => { setShowAdd(false); setEditing(null); load(); }}
        />
      )}
      {showImport && (
        <HolidayImportModal
          onClose={() => setShowImport(false)}
          onDone={() => { setShowImport(false); load(); }}
        />
      )}
    </div>
  );
}

function HolidayModal({ editing, onClose, onSaved }) {
  const isEdit = !!editing?.id && !editing?.system;
  const [form, setForm] = useState(() => ({
    date: editing?.date || new Date().toISOString().slice(0, 10),
    name: editing?.name || "",
    kind: editing?.kind || "company",
    recurring: !!editing?.recurring,
  }));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.date || !form.name.trim()) { toast.error("Tarih ve ad gerekli"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/holidays/${editing.id}`, form);
        toast.success("Güncellendi");
      } else {
        await api.post("/holidays", form);
        toast.success("Eklendi");
      }
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose} data-testid="holiday-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <h3 className="font-display font-extrabold text-slate-900 flex items-center gap-2">
            <CalendarDays size={16} className="text-rose-600"/>
            {isEdit ? "Tatil Düzenle" : "Yeni Tatil Ekle"}
          </h3>
          <button onClick={onClose} className="w-7 h-7 grid place-items-center hover:bg-slate-100 rounded"><X size={14}/></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-700 block mb-1">Tarih</label>
            <DateInput
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm"
              data-testid="holiday-date"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-700 block mb-1">Ad</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Ör: Şirket Pikniği"
              className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm"
              data-testid="holiday-name"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-700 block mb-1">Tür</label>
            <select
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value })}
              className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm"
              data-testid="holiday-kind"
            >
              <option value="religious">Dini Bayram</option>
              <option value="company">Şirket Tatili</option>
              <option value="custom">Özel Tatil</option>
            </select>
          </div>
          <label className="inline-flex items-center gap-2 text-xs text-slate-700 cursor-pointer bg-sky-50 border border-sky-200 rounded-md px-2.5 py-1.5 w-full">
            <input
              type="checkbox"
              checked={form.recurring}
              onChange={(e) => setForm({ ...form, recurring: e.target.checked })}
              data-testid="holiday-recurring"
            />
            <strong>Her yıl tekrar etsin</strong>
            <span className="text-slate-500">— sadece ay/gün değerlendirilir</span>
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-200">
          <button onClick={onClose} className="px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-md">İptal</button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="holiday-save"
            className="px-4 py-2 text-sm font-semibold bg-rose-600 hover:bg-rose-700 text-white rounded-md disabled:opacity-50"
          >
            {saving ? "Kaydediliyor..." : (isEdit ? "Güncelle" : "Ekle")}
          </button>
        </div>
      </div>
    </div>
  );
}


function HolidayImportModal({ onClose, onDone }) {
  const [preview, setPreview] = useState({ years: [], counts: {} });
  const [selected, setSelected] = useState(() => new Set([2025, 2026, 2027, 2028]));
  const [strategy, setStrategy] = useState("skip");
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/holidays/preset");
        setPreview({ years: r.data.years || [], counts: r.data.preview || {} });
        setSelected(new Set(r.data.years || []));
      } catch {
        toast.error("Preset yüklenemedi");
      } finally { setLoading(false); }
    })();
  }, []);

  const toggleYear = (y) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(y)) n.delete(y); else n.add(y);
      return n;
    });
  };

  const runImport = async () => {
    if (selected.size === 0) { toast.error("En az bir yıl seçin"); return; }
    setImporting(true);
    try {
      const r = await api.post("/holidays/import", {
        years: Array.from(selected).sort(),
        strategy,
      });
      setResult(r.data);
      const ins = r.data.inserted_count || 0;
      const skp = r.data.skipped_count || 0;
      toast.success(`${ins} yeni tatil eklendi · ${skp} atlandı`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İçe aktarım başarısız");
    } finally { setImporting(false); }
  };

  const totalPreview = Array.from(selected).reduce(
    (acc, y) => acc + (preview.counts[String(y)] || 0), 0
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose} data-testid="holiday-import-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <h3 className="font-display font-extrabold text-slate-900 flex items-center gap-2">
            <Download size={16} className="text-indigo-600"/>
            Diyanet Dini Bayramları — Toplu İçe Aktar
          </h3>
          <button onClick={onClose} className="w-7 h-7 grid place-items-center hover:bg-slate-100 rounded"><X size={14}/></button>
        </div>

        {!result ? (
          <>
            <div className="p-4 space-y-3">
              <p className="text-xs text-slate-600 leading-relaxed">
                Seçilen yıllar için Diyanet takvimindeki <strong>Ramazan ve Kurban Bayramı</strong> tarihleri
                sisteme eklenir. Sistemde zaten tanımlı tarihler <strong>otomatik atlanır</strong>.
              </p>

              {loading ? (
                <div className="text-xs italic text-slate-400 p-4 text-center">Yükleniyor...</div>
              ) : (
                <>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">Yıllar</div>
                    <div className="grid grid-cols-4 gap-2">
                      {preview.years.map((y) => {
                        const on = selected.has(y);
                        const cnt = preview.counts[String(y)] || 0;
                        return (
                          <button
                            key={y}
                            onClick={() => toggleYear(y)}
                            data-testid={`holiday-import-year-${y}`}
                            className={`py-2 rounded-md border text-sm font-bold transition ${
                              on
                                ? "bg-indigo-600 border-indigo-700 text-white shadow-sm"
                                : "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100"
                            }`}
                          >
                            <div>{y}</div>
                            <div className={`text-[10px] font-normal mt-0.5 ${on ? "text-indigo-100" : "text-slate-500"}`}>{cnt} tatil</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">Çakışma Stratejisi</div>
                    <div className="space-y-1.5">
                      <label className="flex items-start gap-2 text-xs cursor-pointer bg-slate-50 border border-slate-200 rounded-md px-2.5 py-2">
                        <input
                          type="radio"
                          name="strategy"
                          value="skip"
                          checked={strategy === "skip"}
                          onChange={(e) => setStrategy(e.target.value)}
                          data-testid="holiday-import-strategy-skip"
                          className="mt-0.5"
                        />
                        <div>
                          <strong>Atla (önerilen)</strong>
                          <div className="text-slate-500">Aynı tarihte kayıt varsa dokunma, sadece yeni olanları ekle.</div>
                        </div>
                      </label>
                      <label className="flex items-start gap-2 text-xs cursor-pointer bg-slate-50 border border-slate-200 rounded-md px-2.5 py-2">
                        <input
                          type="radio"
                          name="strategy"
                          value="overwrite"
                          checked={strategy === "overwrite"}
                          onChange={(e) => setStrategy(e.target.value)}
                          data-testid="holiday-import-strategy-overwrite"
                          className="mt-0.5"
                        />
                        <div>
                          <strong>Üzerine yaz</strong>
                          <div className="text-slate-500">DB'deki aynı tarihli kayıtlar yeni isimle güncellenir. Sistem tatilleri yine korunur.</div>
                        </div>
                      </label>
                    </div>
                  </div>

                  <div className="bg-indigo-50 border border-indigo-200 rounded-md px-3 py-2 text-xs text-indigo-900">
                    <strong>{selected.size}</strong> yıl seçili · toplam <strong>{totalPreview}</strong> kayıt kontrol edilecek
                  </div>
                </>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-200">
              <button onClick={onClose} className="px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-md">İptal</button>
              <button
                onClick={runImport}
                disabled={importing || loading || selected.size === 0}
                data-testid="holiday-import-run"
                className="px-4 py-2 text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 text-white rounded-md disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                <Download size={13}/>
                {importing ? "İçe aktarılıyor..." : "İçe Aktar"}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="p-4 space-y-3" data-testid="holiday-import-result">
              <div className="flex items-center gap-2 text-emerald-700">
                <CheckCircle2 size={18}/>
                <span className="font-bold">İçe aktarım tamamlandı</span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-emerald-50 border border-emerald-200 rounded-md p-2">
                  <div className="text-2xl font-extrabold text-emerald-700">{result.inserted_count}</div>
                  <div className="text-[10px] uppercase font-bold text-emerald-800">Eklendi</div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-md p-2">
                  <div className="text-2xl font-extrabold text-amber-700">{result.skipped_count}</div>
                  <div className="text-[10px] uppercase font-bold text-amber-800">Atlandı</div>
                </div>
                <div className="bg-sky-50 border border-sky-200 rounded-md p-2">
                  <div className="text-2xl font-extrabold text-sky-700">{result.overwritten_count}</div>
                  <div className="text-[10px] uppercase font-bold text-sky-800">Güncellendi</div>
                </div>
              </div>
              {result.inserted?.length > 0 && (
                <div>
                  <div className="text-xs font-bold uppercase text-slate-700 mb-1">Eklenen Tatiller</div>
                  <div className="max-h-32 overflow-y-auto bg-slate-50 border border-slate-200 rounded-md divide-y divide-slate-100">
                    {result.inserted.map((it, i) => (
                      <div key={i} className="flex items-center gap-2 px-2 py-1 text-xs">
                        <span className="font-mono text-slate-700 min-w-[86px]">{it.date}</span>
                        <span className="text-slate-900">{it.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {result.skipped?.length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer font-bold text-slate-600 hover:text-slate-900">
                    Atlanan {result.skipped.length} kayıt
                  </summary>
                  <div className="mt-1 max-h-32 overflow-y-auto bg-slate-50 border border-slate-200 rounded-md divide-y divide-slate-100">
                    {result.skipped.map((it, i) => (
                      <div key={i} className="flex items-center gap-2 px-2 py-1 text-xs">
                        <span className="font-mono text-slate-500 min-w-[86px]">{it.date}</span>
                        <span className="text-slate-700 flex-1">{it.name}</span>
                        <span className="text-[9px] bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded font-bold uppercase">
                          {it.reason === "system" ? "sistem" : "mevcut"}
                        </span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-200">
              <button
                onClick={onDone}
                data-testid="holiday-import-close"
                className="px-4 py-2 text-sm font-semibold bg-slate-900 hover:bg-slate-800 text-white rounded-md"
              >
                Tamam
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
